import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  untracked,
} from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import {
  type AbstractControl,
  type ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  type ValidationErrors,
  type Validator,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { DEFAULT_TRANSLATION_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage, type TranslatableText } from '@zag/core/common';

import { LanguageService } from '../services/language.service';

const NEW_LINE_KEY = 'Enter';

/**
 * Standalone reactive-forms-friendly input that accepts a
 * {@link TranslatableText} value. Renders an inline tab strip listing every
 * supported language above a single text input (or textarea). Each tab shows
 * a "filled" indicator when its language slot has a non-empty value.
 *
 * The component is intentionally focused on a **single** translatable field —
 * compose multiple instances inside a `<zag-form-field>` for entities with
 * several fields (e.g. `name` + `description`).
 *
 * @example
 * ```html
 * <zag-form-field [label]="'persons.fields.name' | translate"
 *                 [error]="getFieldError('nameTranslations') ?? ''"
 *                 [required]="true">
 *   <zag-translated-input formControlName="nameTranslations" />
 * </zag-form-field>
 * ```
 */
@Component({
  selector: 'zag-translated-input',
  imports: [TranslatePipe, UpperCasePipe],
  templateUrl: './translated-input.html',
  styleUrl: './translated-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TranslatedInput),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TranslatedInput),
      multi: true,
    },
  ],
})
export class TranslatedInput implements ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly languageService = inject(LanguageService);

  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly multiline = input<boolean>(false);
  readonly rows = input<number>(4);
  readonly defaultLanguage = input<SupportedLanguage>(DEFAULT_TRANSLATION_LANGUAGE);
  readonly fieldKey = input<string>('value');
  readonly inputClass = input<string>('');
  readonly ariaDescribedby = input<string | undefined>(undefined);

  /** Two-way bound translations map. */
  readonly value = model<TranslatableText>({});

  /**
   * Optional override for the language list. When provided, only the listed
   * languages are shown as tabs. Pass a single-element array to hide the tab
   * strip entirely (single-language tenants).
   * When omitted, all {@link SUPPORTED_LANGUAGES} are shown.
   */
  readonly availableLanguages = input<readonly SupportedLanguage[] | null>(null);

  readonly languages = computed<readonly SupportedLanguage[]>(() => {
    const override = this.availableLanguages();
    const current = this.languageService.currentLanguage();
    if (override) {
      const inList = override.includes(current) ? [current, ...override.filter(l => l !== current)] : [...override];
      return inList;
    }
    return [current, ...SUPPORTED_LANGUAGES.filter(l => l !== current)];
  });

  /** True when more than one language is available — controls tab strip visibility. */
  readonly showTabStrip = computed(() => this.languages().length > 1);

  /** Currently focused language tab. Local component state. */
  readonly activeLanguage = signal<SupportedLanguage>(this.languageService.getCurrentLanguage());

  /**
   * Tracks whether the user has explicitly clicked a language tab. Once true,
   * the bootstrap-time language sync (the constructor effect below) stops
   * overriding the user's choice. This prevents a race where an async
   * `setLanguage()` call (e.g. from `syncServerPreferences()` on app start)
   * resolves WHILE the user is typing in a non-default tab — the resulting
   * `currentLanguage` emission would otherwise snap `activeLanguage` back to
   * the resolved language and silently route subsequent keystrokes into the
   * wrong translation slot.
   */
  private readonly userHasSelectedLanguage = signal<boolean>(false);

  protected readonly disabledFromHost = signal<boolean>(false);

  /** Resolved disabled flag (input + ControlValueAccessor merged). */
  readonly isDisabled = computed(() => this.disabled() || this.disabledFromHost());

  /** Slot for the currently selected language. */
  readonly activeText = computed<string>(() => {
    const slot = this.value()[this.activeLanguage()];
    if (slot == null) return '';
    const raw = slot[this.fieldKey()];
    return typeof raw === 'string' ? raw : '';
  });

  /**
   * Placeholder shown when the active-language slot is empty.
   *
   * Priority: user's current language text → default language text → explicit placeholder.
   * This lets translators see the source text in their own language when filling in
   * other language slots.
   */
  readonly effectivePlaceholder = computed<string>(() => {
    const active = this.activeLanguage();
    // When the active slot already has content, the placeholder is invisible anyway.
    if (this.activeText().length > 0) return this.placeholder();

    const map = this.value();
    const field = this.fieldKey();

    // 1. Show the user's current language as the translation source hint.
    const currentLang = this.languageService.currentLanguage();
    if (currentLang !== active) {
      const currentRaw = map[currentLang]?.[field];
      if (typeof currentRaw === 'string' && currentRaw.trim().length > 0) {
        return currentRaw;
      }
    }

    // 2. Fall back to the configured default language (en) if different from both.
    const defaultLang = this.defaultLanguage();
    if (defaultLang !== active && defaultLang !== currentLang) {
      const defaultRaw = map[defaultLang]?.[field];
      if (typeof defaultRaw === 'string' && defaultRaw.trim().length > 0) {
        return defaultRaw;
      }
    }

    return this.placeholder();
  });

  /** Languages whose slot has a non-empty value for the configured field. */
  readonly filledLanguages = computed<ReadonlySet<SupportedLanguage>>(() => {
    const filled = new Set<SupportedLanguage>();
    const map = this.value();
    const field = this.fieldKey();
    for (const lang of this.languages()) {
      const raw = map[lang]?.[field];
      if (typeof raw === 'string' && raw.trim().length > 0) filled.add(lang);
    }
    return filled;
  });

  protected readonly tabIds = computed(() => {
    const baseId = this.id() ?? this.name() ?? 'translated-input';
    const tabs: Record<SupportedLanguage, string> = {} as Record<SupportedLanguage, string>;
    for (const lang of this.languages()) {
      tabs[lang] = `${baseId}-tab-${lang}`;
    }
    return tabs;
  });

  protected readonly inputId = computed(() => {
    const baseId = this.id() ?? this.name() ?? 'translated-input';
    return `${baseId}-input-${this.activeLanguage()}`;
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: TranslatableText) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private validatorChange: () => void = () => {};

  constructor() {
    effect(() => {
      const currentLanguage = this.languageService.currentLanguage();

      // Bootstrap-time sync only — once the user has explicitly picked a tab,
      // their choice wins. See `userHasSelectedLanguage` for the full rationale.
      if (!untracked(this.userHasSelectedLanguage) && untracked(this.activeLanguage) !== currentLanguage) {
        this.activeLanguage.set(currentLanguage);
      }

      this.validatorChange();
      this.cdr.markForCheck();
    });
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.required()) return null;
    // Valid when any language slot has a non-empty value — the user may fill
    // whichever language they prefer without being forced to fill the app's
    // current UI language first.
    if (this.filledLanguages().size > 0) return null;
    return { requiredPrimaryLanguage: { language: this.languages()[0] } };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  writeValue(next: TranslatableText | null | undefined): void {
    const sanitized = next ?? {};
    this.value.set(sanitized);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: TranslatableText) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledFromHost.set(isDisabled);
    this.cdr.markForCheck();
  }

  protected selectLanguage(language: SupportedLanguage): void {
    this.userHasSelectedLanguage.set(true);
    this.activeLanguage.set(language);
  }

  protected onTabKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const offset = event.key === 'ArrowRight' ? 1 : -1;
      const langs = this.languages();
      const next = (index + offset + langs.length) % langs.length;
      this.selectLanguage(langs[next]);
      this.focusTab(next);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      this.selectLanguage(this.languages()[0]);
      this.focusTab(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      const langs = this.languages();
      const lastIndex = langs.length - 1;
      this.selectLanguage(langs[lastIndex]);
      this.focusTab(lastIndex);
    }
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const next = target.value;
    const language = this.activeLanguage();
    const field = this.fieldKey();
    const merged: TranslatableText = { ...this.value() };
    const slot = { ...(merged[language] ?? {}) };
    slot[field] = next;
    merged[language] = slot;
    this.value.set(merged);
    this.onChange(merged);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected onTextareaKeydown(event: KeyboardEvent): void {
    // Allow Enter to insert a newline in textarea mode without bubbling to a
    // surrounding form's default submission. The host form intentionally
    // submits via explicit save buttons, never via implicit Enter.
    if (event.key === NEW_LINE_KEY && !event.shiftKey) {
      event.stopPropagation();
    }
  }

  private focusTab(index: number): void {
    const lang = this.languages()[index];
    const tabId = this.tabIds()[lang];
    if (typeof document === 'undefined') return;
    const node = document.getElementById(tabId);
    if (node instanceof HTMLElement) node.focus();
  }
}
